import { useCallback, useEffect, useRef, useState } from 'react'
import {
  canStartPull,
  resolvePullDistance,
  shouldTriggerRefresh,
} from '@/application/pwa/pullToRefresh'

/**
 * True when the app is running as an installed PWA rather than a browser tab.
 * iOS exposes this only through the legacy `navigator.standalone` flag; every
 * other engine reports it via the `display-mode` media query.
 */
export const isStandaloneDisplay = (): boolean => {
  // `navigator.standalone` is a non-standard iOS-only flag, absent from the
  // DOM lib types; declared in `vite-env.d.ts`.
  const iosStandalone = navigator.standalone === true
  // `?.matches` guards engines (and test environments) where `matchMedia`
  // exists but returns nothing for an unrecognised query.
  const displayModeStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)')?.matches === true

  return iosStandalone || displayModeStandalone
}

interface UsePullToRefreshOptions {
  /** Runs when the gesture commits. The indicator stays up until it settles. */
  onRefresh: () => Promise<void> | void
  /**
   * Set false to disable the gesture entirely (defaults to on). The hook is
   * additionally inert outside standalone mode, where the browser provides
   * its own pull-to-refresh.
   */
  enabled?: boolean
}

interface UsePullToRefreshResult {
  /** How far the indicator should currently be pulled down, in px. */
  pullDistance: number
  /** True while `onRefresh` is in flight. */
  isRefreshing: boolean
  /** True once the pull has passed the commit threshold. */
  hasReachedThreshold: boolean
}

/**
 * The scroll offset that matters for the gesture: the nearest scrollable
 * ancestor of the touch target, falling back to the document. On mobile the
 * page itself scrolls, but `<main>` is its own scroller from `md:` up.
 */
const currentScrollTop = (target: EventTarget | null): number => {
  let node = target instanceof Element ? target : null

  while (node) {
    const { overflowY } = getComputedStyle(node)
    const scrollable = overflowY === 'auto' || overflowY === 'scroll'
    if (scrollable && node.scrollHeight > node.clientHeight) return node.scrollTop
    node = node.parentElement
  }

  return window.scrollY || document.documentElement.scrollTop
}

/**
 * Implements pull-to-refresh for the installed PWA, where iOS provides none.
 *
 * Listens on the document rather than a specific element, so it works whether
 * the page or an inner region is the scroller. Touch listeners are passive
 * except for `touchmove`, which must be able to `preventDefault()` to stop the
 * page rubber-banding while the indicator is showing.
 */
export const usePullToRefresh = ({
  onRefresh,
  enabled = true,
}: UsePullToRefreshOptions): UsePullToRefreshResult => {
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Gesture bookkeeping lives in refs: these change on every touch frame and
  // must not each trigger a re-render.
  const startYRef = useRef<number | null>(null)
  const rawDistanceRef = useRef(0)
  // Read inside listeners that are bound once, so it must not go stale.
  const isRefreshingRef = useRef(false)

  const runRefresh = useCallback(async () => {
    isRefreshingRef.current = true
    setIsRefreshing(true)
    try {
      await onRefresh()
    } finally {
      isRefreshingRef.current = false
      setIsRefreshing(false)
      setPullDistance(0)
    }
  }, [onRefresh])

  useEffect(() => {
    if (!enabled) return
    // A browser tab already has native pull-to-refresh; adding a second one
    // would mean two competing gestures.
    if (!isStandaloneDisplay()) return

    const resetGesture = (): void => {
      startYRef.current = null
      rawDistanceRef.current = 0
    }

    const handleTouchStart = (event: TouchEvent): void => {
      if (isRefreshingRef.current) return
      // Multi-touch is a pinch or similar, never a pull.
      if (event.touches.length !== 1) return
      if (!canStartPull(currentScrollTop(event.target))) return

      startYRef.current = event.touches[0].clientY
      rawDistanceRef.current = 0
    }

    const handleTouchMove = (event: TouchEvent): void => {
      const startY = startYRef.current
      if (startY === null || isRefreshingRef.current) return

      const delta = event.touches[0].clientY - startY

      // An upward drag is an ordinary scroll — hand the gesture back.
      if (delta <= 0) {
        resetGesture()
        setPullDistance(0)
        return
      }

      rawDistanceRef.current = delta
      // Suppress the browser's own overscroll so only the indicator moves.
      if (event.cancelable) event.preventDefault()
      setPullDistance(resolvePullDistance(delta))
    }

    const handleTouchEnd = (): void => {
      const rawDistance = rawDistanceRef.current
      resetGesture()

      if (rawDistance > 0 && shouldTriggerRefresh(rawDistance) && !isRefreshingRef.current) {
        void runRefresh()
        return
      }
      setPullDistance(0)
    }

    // `touchmove` is explicitly non-passive so `preventDefault()` applies.
    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd, { passive: true })
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true })

    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [enabled, runRefresh])

  return {
    pullDistance,
    isRefreshing,
    hasReachedThreshold: shouldTriggerRefresh(rawDistanceRef.current),
  }
}
